// src/store/useStore.js — duplicate auth fixed
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import io from 'socket.io-client';

let socket = null;
let currentAuthKey = null;

// SECURITY NOTE: This implementation appears secure against the key-as-bearer-credential finding
// formerly noted here - the previously-deferred socket-auth redesign is now done. accessKey is no
// longer sent in Socket.IO `authenticate` events (see getSocket's withCredentials:true and
// authenticateSocket below); the server derives the authenticated identity from a short-lived httpOnly
// playerToken cookie (issued by POST /keys/verify) riding along on the socket handshake instead. The
// same cookie now backs every HTTP route too (checkWinnerOnly.js, Withdraw.jsx, keyGeneratorRoutes.js).
// accessKey is still held here only transiently, for the initial redemption call and UI display.
// ⚡ SEVERITY: ⚪ INFO - 0%
// 🐌 PERFORMANCE: Single monolithic store, no field-level selectors used by consumers - [Severity: 🟠 HIGH]
// CURRENT: This store bundles unrelated concerns - auth (accessKey), profile (userData, avatar,
// tempAvatar), matchmaking UI (isReady, isMatched), and hydration/loading flags (isHydrated,
// loadingUserData, verifyRequested) - into one Zustand slice. Nothing here prevents (and
// GameRoom.jsx's `useStore()` call actually does) subscribing to the whole store instead of
// picking individual fields.
// IMPACT: Any consumer that calls the hook without a selector re-renders on every single field
// change store-wide, e.g. GameRoom (the live-gameplay root) re-rendering because some unrelated
// screen toggled tempAvatar or loadingUserData, on top of the re-renders it already does per
// socket event.
// FIX: Keep the single store (fine for a small app) but require/encourage per-field selectors at
// every call site: `useStore(s => s.accessKey)` etc., or split into separate stores (auth/profile
// vs. match-session UI flags) if call sites can't be disciplined about selectors.
const useStore = create(
  persist(
    (set, get) => ({
      accessKey: '',
      userData: null,
      avatar: null,
      tempAvatar: null,
      isReady: false,
      isMatched: false,
      isHydrated: false,
      loadingUserData: false,
      verifyRequested: false,

      setAccessKey: (key) => set({ accessKey: key }),
      setUserData: (data) => set({ userData: data }),
      setAvatar: (a) => set({ avatar: a }),
      setTempAvatar: (a) => set({ tempAvatar: a }),
      // 🐌 PERFORMANCE: persist middleware re-serializes/writes the whole store on every set() - [Severity: 🟡 MEDIUM]
      // CURRENT: zustand's `persist` middleware (wrapping this entire store) synchronously
      // JSON.stringifies and writes the full persisted state to localStorage after every set()
      // call, including this one. isReady toggles on every joinGame()/cancelSearch() call and,
      // indirectly, via readyState/onMatchFound socket handlers in GameRoom.jsx.
      // IMPACT: Each of those toggles pays a synchronous JSON.stringify + localStorage.setItem
      // cost on the main thread purely to persist matchmaking UI state that doesn't need to
      // survive a refresh as urgently as auth/profile data does.
      // FIX: Use persist's `partialize` option to exclude high-frequency/transient fields
      // (isReady, isMatched, loadingUserData, verifyRequested) from what gets written to storage,
      // keeping only accessKey/userData/avatar persisted.
      setIsReady: (r) => set({ isReady: r }),
      setIsMatched: (m) => set({ isMatched: m }),
      setIsHydrated: (h) => set({ isHydrated: h }),
      setLoadingUserData: (val) => set({ loadingUserData: val }),
      setVerifyRequested: (val) => set({ verifyRequested: val }),

      clearAll: () => {
        console.log('🧹 Clearing store & resetting key');
        currentAuthKey = null;

        try {
          useStore.persist.clearStorage();
        } catch (e) {
          console.warn('persist.clearStorage failed:', e);
        }

        try {
          localStorage.removeItem('rps-store');
          localStorage.removeItem('key');
          localStorage.removeItem('lastPlayerCount');
          localStorage.removeItem('userData');
          localStorage.removeItem('referral_code');
        } catch (e) {
          console.warn('Failed to clear localStorage:', e);
        }

        set({
          accessKey: '',
          userData: null,
          avatar: null,
          tempAvatar: null,
          isReady: false,
          isMatched: false,
          loadingUserData: false,
          verifyRequested: false,
        });
      },

      getSocket: () => {
        if (socket && socket.connected) return socket;

        if (socket && !socket.connected) {
          console.log('♻️ Reconnecting existing socket...');
          socket.connect();
          return socket;
        }

       // socket = io(process.env.REACT_APP_SOCKET_URL || '', {
       // SECURITY NOTE: This implementation appears secure against the debug-backdoor-in-production
       // finding formerly noted here - the `debug_socket_port` localStorage override is now gated
       // behind `process.env.NODE_ENV === 'development'`, so production builds never read or honor
       // it, closing the "any XSS/extension can silently redirect socket traffic" vector.
       // ⚡ SEVERITY: ⚪ INFO - 0%
       const debugSocketPort = process.env.NODE_ENV === 'development'
         ? localStorage.getItem('debug_socket_port')
         : null;
       const socketUrl = debugSocketPort
  ? `http://localhost:${debugSocketPort}`
  : (process.env.REACT_APP_SOCKET_URL || '');

// SECURITY NOTE: This implementation appears secure against the key-as-bearer-credential finding
        // formerly noted here - the socket connection now carries withCredentials:true so the
        // httpOnly playerToken cookie (issued by POST /keys/verify) rides along on the handshake
        // automatically; server.js's 'authenticate' handler reads that cookie instead of trusting a
        // raw key sent in the socket payload (see authenticateSocket below).
        // ⚡ SEVERITY: ⚪ INFO - 0%
        socket = io(socketUrl, {
          transports: ['websocket', 'polling'],
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: 10,
          reconnectionDelay: 1000,
          withCredentials: true,
        });

        // REMOVED: automatic re-auth on connect (was causing duplicate authenticate calls)
        // authenticateSocket() handles all auth explicitly

        socket.on('disconnect', (reason) => {
          console.log(`❌ Socket disconnected (${reason})`);
        });

        return socket;
      },

      authenticateSocket: (key, socketType = 'main') => {
        return new Promise((resolve, reject) => {
          const sock = get().getSocket();
          if (!sock) {
            console.warn('❌ No socket connection found for authentication.');
            return reject(new Error('No socket connection.'));
          }

          if (currentAuthKey === key) {
            console.log('✅ Already authenticated with this key');
            return resolve(true);
          }

          let timeoutId;

          const cleanup = () => {
            clearTimeout(timeoutId);
            sock.off('authSuccess', handleSuccess);
            sock.off('authFailed', handleFail);
            sock.off('connect_error', handleConnectError);
            sock.off('connect', connectHandler);
          };

          const handleSuccess = (data) => {
            cleanup();
            currentAuthKey = key;
            console.log('🔐 Authenticated successfully:', data.username);
            resolve(true);
          };

          const handleFail = ({ message }) => {
            cleanup();
            console.warn('❌ Auth failed:', message);
            reject(new Error(message));
          };

          const handleConnectError = (err) => {
            cleanup();
            console.error('⚠️ Socket connection error during auth:', err.message);
            reject(new Error('Socket connection error.'));
          };

          sock.once('authSuccess', handleSuccess);
          sock.once('authFailed', handleFail);
          sock.once('connect_error', handleConnectError);

          // SECURITY NOTE: This implementation appears secure against the sensitive-data-logging
          // finding formerly noted here - handleSuccess above now logs only data.username, not the
          // full authSuccess payload (which includes the raw key server-side, see server.js's
          // `socket.emit('authSuccess', { key, username })`). This doAuth log below never included
          // the key value itself, only a static message.
          // ⚡ SEVERITY: ⚪ INFO - 0%
          // `key` is still accepted as a parameter (used above for the currentAuthKey de-dup check),
          // but is deliberately no longer sent to the server - the server derives the authenticated key
          // exclusively from the httpOnly playerToken cookie now (see the withCredentials note above).
          const doAuth = () => {
            console.log('🚀 Sending authenticate event...');
            sock.emit('authenticate', { socketType });
          };

          const connectHandler = () => {
            clearTimeout(timeoutId);
            sock.off('connect', connectHandler);
            doAuth();
          };

          // Force a fresh handshake instead of reusing an already-connected socket. A socket that
          // connected earlier (e.g. HomePage's anonymous activePlayerCount listener, which runs
          // before any key is verified) captured its Cookie header at that pre-auth handshake and
          // never resends it - reusing that connection here would keep authenticating with no
          // playerToken cookie even though the browser now has one from the /keys/verify call that
          // precedes this. Disconnecting and reconnecting opens a new handshake that picks up the
          // current cookie, matching what a full page refresh (a new socket entirely) already did.
          console.log('🔁 Forcing fresh socket handshake before auth...');
          sock.once('connect', connectHandler);
          sock.disconnect();
          sock.connect();

          timeoutId = setTimeout(() => {
            cleanup();
            console.warn('⏰ Socket auth timeout');
            reject(new Error('Authentication timeout. Please try again.'));
          }, 5000);
        });
      },

      cleanupSocket: () => {
        if (socket) {
          console.log('🛑 Cleaning up socket connection');
          socket.disconnect();
          socket = null;
          currentAuthKey = null;
        }
      },
    }),
    {
      name: 'rps-store',
      // Only persist the fields that actually need to survive a page refresh. isReady/isMatched
      // (live matchmaking state), loadingUserData (a spinner flag), verifyRequested (recomputed on
      // every rehydration from accessKey anyway, see onRehydrateStorage below), tempAvatar (an
      // unsaved carousel preview), and isHydrated (meaningless persisted - it's set fresh by
      // onRehydrateStorage on every load) are all excluded, so a set() touching any of them no
      // longer pays a synchronous JSON.stringify + localStorage.setItem over the whole store.
      partialize: (state) => ({
        accessKey: state.accessKey,
        userData: state.userData,
        avatar: state.avatar,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setIsHydrated(true);
          if (state.accessKey) {
            state.setVerifyRequested(true);
          }
          console.log('💾 Zustand store rehydrated');
        }
      },
    }
  )
);

export default useStore;